import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import ImageLightbox from "@/components/ImageLightbox";
import SearchBar from "@/components/SearchBar";
import BookmarkBtn from "@/components/BookmarkBtn";
import UpgradeGuestModal from "@/components/UpgradeGuestModal";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, ArrowLeft, ArrowRight, Image as ImageIcon, Mic, Square, Lock } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL || "";

export default function DMThread() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [other, setOther] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { src, filename }
  const [searchQ, setSearchQ] = useState("");
  const [bookmarks, setBookmarks] = useState(new Set());
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordStartRef = useRef(0);

  const isGuest = !!user?.is_guest;

  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const initialLoadRef = useRef(true);

  const load = useCallback(async () => {
    try {
      // Ask for up to 500 in one shot — covers the vast majority of DM threads
      // without needing the Load Older button.
      const { data } = await api.get(`/dms/${userId}?limit=500`);
      setOther(data.with);
      const fresh = data.messages || [];
      setMsgs((cur) => {
        // On the very first load, replace outright.
        if (initialLoadRef.current) {
          return fresh;
        }
        // Subsequent polls: merge freshest page with anything already loaded
        // (incl. older pages) so "Load older" history isn't wiped every 4s.
        const byId = new Map();
        for (const m of cur) byId.set(m.id, m);
        for (const m of fresh) byId.set(m.id, m);
        return Array.from(byId.values()).sort((a, b) =>
          (a.created_at || "").localeCompare(b.created_at || "")
        );
      });
      if (initialLoadRef.current) {
        // Only trust has_more on the very first load — after that, loadOlder
        // owns it. We don't want the 4s poll re-showing the button once the
        // user has already paged everything in.
        setHasMore(!!data.has_more);
        initialLoadRef.current = false;
        setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
      }
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, [userId]);

  const loadOlder = useCallback(async () => {
    if (loadingOlder || !hasMore || msgs.length === 0) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    const prevTop = el ? el.scrollTop : 0;
    try {
      const oldest = msgs[0]?.created_at;
      const { data } = await api.get(
        `/dms/${userId}?limit=500&before=${encodeURIComponent(oldest)}`
      );
      const older = data?.messages || [];
      if (older.length) {
        setMsgs((cur) => {
          const byId = new Map();
          for (const m of older) byId.set(m.id, m);
          for (const m of cur) byId.set(m.id, m);
          return Array.from(byId.values()).sort((a, b) =>
            (a.created_at || "").localeCompare(b.created_at || "")
          );
        });
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = prevTop + (scrollRef.current.scrollHeight - prevHeight);
          }
        });
      }
      setHasMore(!!data?.has_more);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoadingOlder(false);
    }
  }, [userId, msgs, hasMore, loadingOlder]);

  useEffect(() => {
    // Reset pagination flags whenever we open a different thread.
    initialLoadRef.current = true;
    setMsgs([]);
    setHasMore(false);
    load();
    // Safety-net poll every 30s in case the DM WebSocket drops. Real-time
    // updates arrive via the `nw:new-dm` window event below.
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Real-time DM push — append new DMs in this thread the instant they arrive.
  useEffect(() => {
    if (!userId || !user?.id) return;
    const onNewDM = (ev) => {
      const m = ev?.detail?.message;
      if (!m) return;
      const inThread =
        (m.from_id === user.id && m.to_id === userId) ||
        (m.from_id === userId && m.to_id === user.id);
      if (!inThread) return;
      setMsgs((cur) => {
        if (cur.some((x) => x.id === m.id)) return cur;
        return [...cur, m].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      });
      // Auto-scroll only if the viewer is already near the bottom.
      const el = scrollRef.current;
      if (el) {
        const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 120;
        if (nearBottom) {
          setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 30);
        }
      }
    };
    window.addEventListener("nw:new-dm", onNewDM);
    return () => window.removeEventListener("nw:new-dm", onNewDM);
  }, [userId, user?.id]);

  // Load bookmarks so stars render pre-selected.
  useEffect(() => {
    if (!user || user.is_guest) return;
    let alive = true;
    api.get("/bookmarks").then(({ data }) => {
      if (!alive) return;
      setBookmarks(new Set((data.bookmarks || []).map((b) => b.message_id)));
    }).catch(() => {});
    return () => { alive = false; };
  }, [user?.id, user?.is_guest]);

  // Handle ?highlight=<msg_id> jump-to.
  useEffect(() => {
    const hid = searchParams.get("highlight");
    if (!hid || msgs.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`dm-msg-${hid}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("msg-flash");
        setTimeout(() => el.classList.remove("msg-flash"), 1800);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchParams, msgs.length]);

  const sendText = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const body = input;
    setInput("");
    try {
      const { data } = await api.post("/dms", { to_user_id: userId, body });
      // Optimistically append the sent message — the WS echo will de-dupe by id.
      if (data && data.id) {
        setMsgs((cur) => (cur.some((x) => x.id === data.id) ? cur : [...cur, data]));
        setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 30);
      }
    } catch (err) {
      setInput(body);
      toast.error(formatErr(err));
    }
  };

  const sendAttachment = async (kind, attachment_url, attachment_type, duration_ms) => {
    try {
      const { data } = await api.post("/dms", {
        to_user_id: userId,
        body: "",
        kind,
        attachment_url,
        attachment_type,
        ...(duration_ms ? { duration_ms } : {}),
      });
      if (data && data.id) {
        setMsgs((cur) => (cur.some((x) => x.id === data.id) ? cur : [...cur, data]));
        setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 30);
      }
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const pickImage = () => fileInputRef.current?.click();

  const onImagePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await sendAttachment("image", data.url, data.content_type);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Mic not supported on this browser."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recordStartRef.current = Date.now();
      mr.ondataavailable = (ev) => ev.data.size > 0 && audioChunksRef.current.push(ev.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        const duration = Date.now() - recordStartRef.current;
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", new File([blob], "voice.webm", { type: blob.type }));
          const { data } = await api.post("/uploads/audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
          await sendAttachment("audio", data.url, data.content_type, duration);
        } catch (err) {
          toast.error(formatErr(err));
        } finally {
          setUploading(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error("Mic permission denied.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopBar />
      <div className="flex-1 flex flex-col max-w-3xl w-full mx-auto" data-testid="dm-thread">
        <div className="border-b border-white/10 p-4 flex items-center gap-3">
          <Link to="/dm" className="text-white/40 hover:text-acid"><ArrowLeft className="w-5 h-5" /></Link>
          {other && (
            <>
              <img src={other.avatar} alt="" className="w-9 h-9 object-cover border border-white/10" />
              <div>
                <div className="font-heading text-2xl leading-none">@{other.username}</div>
                <div className="font-mono text-[10px] text-white/40 tracking-widest">// PRIVATE</div>
              </div>
            </>
          )}
        </div>

        {user && !user.is_guest && (
          <SearchBar onQuery={setSearchQ} placeholder="Search this DM…" testid="dm-search" />
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="dm-messages">
          {hasMore && (
            <div className="flex justify-center pb-2">
              <button
                type="button"
                onClick={loadOlder}
                disabled={loadingOlder}
                data-testid="dm-load-older-btn"
                className="font-mono text-[10px] px-4 py-2 border-2 border-white/20 text-white/70 hover:border-acid hover:text-acid uppercase tracking-widest disabled:opacity-50"
              >
                {loadingOlder ? "LOADING…" : "Load older messages"}
              </button>
            </div>
          )}
          {msgs.length === 0 && <div className="font-mono text-white/30 text-sm">// no messages · say hi</div>}
          {(searchQ.trim()
            ? msgs.filter((m) => (m.body || "").toLowerCase().includes(searchQ.trim().toLowerCase()))
            : msgs
          ).map((m) => {
            const mine = m.from_id === user?.id;
            const isWelcome = m.kind === "welcome" && Array.isArray(m.rooms) && m.rooms.length > 0;
            const isImage = m.kind === "image" && m.attachment_url;
            const isAudio = m.kind === "audio" && m.attachment_url;
            const isBookmarked = bookmarks.has(m.id);
            return (
              <div key={m.id} id={`dm-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] p-3 border ${mine ? "border-acid" : "border-white/20"}`}>
                  <div className="font-mono text-[10px] text-white/50 flex items-center gap-2">
                    <span>{mine ? "YOU" : `@${m.from_username}`} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    {user && !user.is_guest && m.from_id !== "nitewire-bot-0000-0000-0000-000000000001" && (
                      <BookmarkBtn
                        messageId={m.id}
                        sourceType="dm"
                        sourceId={other?.id || userId}
                        bookmarked={isBookmarked}
                        onChange={(is) => setBookmarks((s) => {
                          const n = new Set(s);
                          if (is) n.add(m.id); else n.delete(m.id);
                          return n;
                        })}
                        size="w-3 h-3"
                      />
                    )}
                  </div>

                  {isImage ? (
                    <button
                      type="button"
                      onClick={() => setLightbox({ src: `${backend}${m.attachment_url}`, filename: `dm-${m.id}.png` })}
                      className="block mt-1 p-0 border-0 bg-transparent cursor-zoom-in group/img"
                      data-testid={`dm-image-btn-${m.id}`}
                      title="Click to enlarge / download"
                    >
                      <img
                        src={`${backend}${m.attachment_url}`}
                        alt=""
                        className="max-w-xs max-h-64 border border-white/10 group-hover/img:border-acid transition-colors"
                        loading="lazy"
                        data-testid={`dm-image-${m.id}`}
                      />
                    </button>
                  ) : isAudio ? (
                    <div className="flex items-center gap-2 border border-white/10 p-2 mt-1 w-fit" data-testid={`dm-audio-${m.id}`}>
                      <audio controls preload="metadata" src={`${backend}${m.attachment_url}`} className="max-w-xs" />
                      {m.duration_ms ? (
                        <span className="font-mono text-[10px] text-white/50">{Math.round(m.duration_ms / 1000)}s</span>
                      ) : null}
                    </div>
                  ) : (
                    m.body && (
                      <div className="font-body text-sm mt-1" style={{ color: m.from_color || "#F4F4F0" }}>{m.body}</div>
                    )
                  )}

                  {isWelcome && (
                    <div className="mt-3 space-y-2" data-testid="welcome-room-picks">
                      {m.rooms.map((r) => (
                        <Link
                          key={r.slug}
                          to={`/room/${r.slug}`}
                          data-testid={`welcome-room-${r.slug}`}
                          className="group flex items-center justify-between gap-3 p-3 border-2 border-white/15 hover:border-acid hover:bg-acid/5 transition-colors"
                          style={{ borderLeftColor: r.color || "#D4FF00", borderLeftWidth: "4px" }}
                        >
                          <div className="min-w-0">
                            <div className="font-heading text-lg uppercase tracking-wider leading-none">{r.name}</div>
                            {r.topic && (
                              <div className="font-mono text-[10px] text-white/50 mt-1 truncate">{r.topic}</div>
                            )}
                          </div>
                          <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-acid shrink-0" />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={sendText} className="border-t border-white/10 p-4 flex items-center gap-2">
          <input
            data-testid="dm-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="input-brutal flex-1"
            placeholder={recording ? "recording voice note…" : "say something private…"}
            maxLength={1000}
            disabled={recording}
          />

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onImagePicked}
            data-testid="dm-image-file"
          />

          {isGuest ? (
            <button
              type="button"
              onClick={() => setUpgradeOpen(true)}
              className="font-mono text-[10px] px-3 py-2 border-2 border-acid/40 text-acid hover:bg-acid hover:text-obsidian uppercase tracking-widest flex items-center gap-1"
              data-testid="dm-guest-upgrade"
              title={`Add email + password to keep @${user?.username}`}
            >
              <Lock className="w-3 h-3" /> Voice + Images
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={pickImage}
                disabled={uploading || recording}
                className="font-mono text-xs px-3 py-2 border-2 border-white/20 text-white hover:border-acid hover:text-acid uppercase tracking-widest flex items-center gap-1 disabled:opacity-40"
                data-testid="dm-image-btn"
                title="Send an image"
              >
                <ImageIcon className="w-4 h-4" />
              </button>
              {!recording ? (
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={uploading}
                  className="font-mono text-xs px-3 py-2 border-2 border-white/20 text-white hover:border-acid hover:text-acid uppercase tracking-widest flex items-center gap-1 disabled:opacity-40"
                  data-testid="dm-record-btn"
                  title="Record a voice note"
                >
                  <Mic className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopRecording}
                  className="font-mono text-xs px-3 py-2 border-2 border-blaze text-blaze hover:bg-blaze hover:text-obsidian uppercase tracking-widest flex items-center gap-1 animate-pulse"
                  data-testid="dm-stop-record-btn"
                  title="Stop recording"
                >
                  <Square className="w-4 h-4" />
                </button>
              )}
            </>
          )}

          <button
            data-testid="dm-send"
            type="submit"
            className="btn-acid flex items-center gap-2"
            disabled={!input.trim() || recording || uploading}
          >
            <Send className="w-4 h-4" /> SEND
          </button>
        </form>
      </div>

      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          downloadName={lightbox.filename}
          onClose={() => setLightbox(null)}
        />
      )}

      <UpgradeGuestModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}
